import type { ApiResponse, ApiErrorResponse, ErrorCode } from '@hisob/types';

export class AdminApiClientError extends Error {
  public code: ErrorCode | string;
  public requestId: string;
  public status: number;
  public details?: unknown;

  constructor(message: string, code: ErrorCode | string, requestId: string, status: number, details?: unknown) {
    super(message);
    this.name = 'AdminApiClientError';
    this.code = code;
    this.requestId = requestId;
    this.status = status;
    this.details = details;
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

let authToken: string | null = null;
let onUnauthorizedCallback: (() => void) | null = null;

export function setApiAuthToken(token: string | null) {
  authToken = token;
}

export function getApiAuthToken(): string | null {
  return authToken;
}

export function setUnauthorizedHandler(callback: () => void) {
  onUnauthorizedCallback = callback;
}

function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `req_${crypto.randomUUID()}`;
  }
  return `req_${Math.random().toString(36).substring(2, 15)}`;
}

export interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

export async function apiRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { params, headers = {}, ...rest } = options;

  let url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += (url.includes('?') ? '&' : '?') + queryString;
    }
  }

  const requestId = generateRequestId();
  const requestHeaders = new Headers(headers);

  requestHeaders.set('Accept', 'application/json');
  requestHeaders.set('X-Request-ID', requestId);

  if (authToken && !requestHeaders.has('Authorization')) {
    requestHeaders.set('Authorization', `Bearer ${authToken}`);
  }

  if (rest.body && !(rest.body instanceof FormData) && !requestHeaders.has('Content-Type')) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  try {
    const response = await fetch(url, {
      ...rest,
      headers: requestHeaders,
    });

    const responseRequestId = response.headers.get('X-Request-ID') || requestId;

    if (!response.ok) {
      if (response.status === 401) {
        if (onUnauthorizedCallback) {
          onUnauthorizedCallback();
        }
      }

      let errorData: ApiErrorResponse | null = null;
      try {
        errorData = await response.json();
      } catch {
        // Not a JSON response
      }

      const code = errorData?.error?.code || (response.status === 403 ? 'FORBIDDEN' : 'INTERNAL_ERROR');
      const message = errorData?.error?.message || response.statusText || 'An unexpected error occurred';
      const details = errorData?.error?.details;

      throw new AdminApiClientError(message, code, responseRequestId, response.status, details);
    }

    if (response.status === 204) {
      return {} as T;
    }

    const json: ApiResponse<T> = await response.json();
    return json.data;
  } catch (err: any) {
    if (err instanceof AdminApiClientError) {
      throw err;
    }
    // Network or parse error
    throw new AdminApiClientError(
      err.message || 'Network connection failed. Please check connectivity.',
      'NETWORK_ERROR',
      requestId,
      0
    );
  }
}

/**
 * Checks whether an error is caused by backend offline, connection refused, or proxy failure.
 */
export function isBackendUnavailableError(err: unknown): boolean {
  if (!err) return false;
  if (err instanceof AdminApiClientError) {
    if (err.code === 'NETWORK_ERROR' || err.status === 0) return true;
    if (err.status === 502 || err.status === 503 || err.status === 504) return true;
    const msg = String(err.message || '').toLowerCase();
    if (
      msg.includes('econnrefused') ||
      msg.includes('failed to fetch') ||
      msg.includes('network connection failed') ||
      msg.includes('proxy error') ||
      msg.includes('econnreset')
    ) {
      return true;
    }
  }
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (
      msg.includes('failed to fetch') ||
      msg.includes('econnrefused') ||
      msg.includes('network error')
    ) {
      return true;
    }
  }
  return false;
}

